import { BodyText, Button, Heading, Icon } from '@postscript/components';
import React from 'react';
import styled from 'styled-components';
import { logButtonClickEvent, PRODUCT_AREAS } from 'utils/events';

export type NotificationComponentProps = {
  id: string;
  heading?: string;
  description?: React.ReactNode;
  icon?: () => JSX.Element;
  primaryAction?: {
    text: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
  };
  secondaryAction?: {
    text: string;
    onClick?: React.MouseEventHandler<HTMLButtonElement>;
  };
  showButtons?: boolean;
  dismissible?: boolean;
  onDismiss?: () => void;
  'data-cy'?: string;
};

const StyledNotificationContainer = styled.div`
  display: flex;
  gap: var(--spacing-1);
  align-items: flex-start;
  padding: 0 0 var(--spacing-4) 0;
  border-bottom: 1px solid var(--border-color-dim);

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  flex-grow: 1;
  min-width: 0;
`;

const StyledHeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--spacing-1);
  padding-top: 3px;
`;

const StyledHeading = styled(Heading)`
  flex-grow: 1;
  min-width: 0;
  margin: 0;
`;

const StyledButtonRow = styled.div`
  display: flex;
  gap: var(--spacing-1);
  align-items: flex-start;
  padding-top: var(--spacing-1);
  width: 100%;
`;

const StyledButtonContainer = styled.div`
  display: flex;
  gap: var(--spacing-1);
  align-items: flex-start;
  flex-grow: 1;
  min-width: 0;
`;

const NotificationComponent = ({
  id,
  heading,
  description,
  icon,
  primaryAction,
  secondaryAction,
  showButtons = true,
  dismissible,
  onDismiss,
  'data-cy': dataCy,
  ...props
}: NotificationComponentProps) => {
  const isDismissible = Boolean(dismissible && onDismiss);

  // Wrap action handlers with tracking
  const handlePrimaryActionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (primaryAction) {
      logButtonClickEvent(event, {
        action_type: 'primary',
        location: 'notifications drawer',
        notification_heading: heading,
        notification_id: id,
        product_area: PRODUCT_AREAS.GLOBAL,
      });
      primaryAction.onClick?.(event);
    }
  };

  const handleSecondaryActionClick = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    if (secondaryAction) {
      logButtonClickEvent(event, {
        action_type: 'secondary',
        location: 'notifications drawer',
        notification_heading: heading,
        notification_id: id,
        product_area: PRODUCT_AREAS.GLOBAL,
      });
      secondaryAction.onClick?.(event);
    }
  };

  return (
    <StyledNotificationContainer
      data-cy={dataCy}
      data-notification-id={id}
      {...props}
    >
      {icon && <Icon component={icon} size={24} />}

      <StyledContentContainer>
        {heading && (
          <StyledHeaderContainer>
            <StyledHeading forwardedAs="h3" size="xx-small">
              {heading}
            </StyledHeading>
          </StyledHeaderContainer>
        )}

        {description && <BodyText size="small">{description}</BodyText>}

        {(showButtons && (primaryAction || secondaryAction)) ||
        isDismissible ? (
          <StyledButtonRow>
            <StyledButtonContainer>
              {showButtons && primaryAction && (
                <Button
                  variant="primary"
                  size="small"
                  onClick={handlePrimaryActionClick}
                >
                  {primaryAction.text}
                </Button>
              )}
              {showButtons && secondaryAction && (
                <Button
                  variant="secondary"
                  size="small"
                  onClick={handleSecondaryActionClick}
                >
                  {secondaryAction.text}
                </Button>
              )}
              {isDismissible && (
                <Button
                  variant={
                    showButtons && secondaryAction ? 'text' : 'secondary'
                  }
                  size="small"
                  onClick={onDismiss}
                  aria-label="Dismiss notification"
                >
                  Dismiss
                </Button>
              )}
            </StyledButtonContainer>
          </StyledButtonRow>
        ) : null}
      </StyledContentContainer>
    </StyledNotificationContainer>
  );
};

export default NotificationComponent;
